import React from 'react';
import { Tag, Alert, Steps, Typography } from 'antd';
import {
  RocketOutlined, BankOutlined, LockOutlined, BarChartOutlined,
  TeamOutlined, WarningOutlined, DollarOutlined, ToolOutlined,
  AuditOutlined, CalendarOutlined, InboxOutlined, FileSearchOutlined,
  SafetyCertificateOutlined, CheckCircleOutlined, MailOutlined,
  FileTextOutlined, HomeOutlined, CheckOutlined,
} from '@ant-design/icons';

const { Paragraph, Text } = Typography;

// ─── Types ───────────────────────────────────────────────────────────────────

export interface TutorialStep {
  title: string;
  content: string;
  navigate?: string;
  tip?: string;
  warning?: string;
}

export interface Tutorial {
  id: string;
  title: string;
  description: string;
  category: string;
  roles: string[];
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  estimatedTime: string;
  tags: string[];
  icon: React.ReactNode;
  steps: TutorialStep[];
}

export interface TutorialProgress {
  completed: boolean;
  stepsCompleted: number[];
}

export type ProgressMap = Record<string, TutorialProgress>;

// ─── Category Config ──────────────────────────────────────────────────────────

export const CATEGORY_COLORS: Record<string, string> = {
  'Getting Started':          '#6366f1',
  'Property Administration':  '#0ea5e9',
  'Resident Management':      '#14b8a6',
  'Financial Management':     '#22c55e',
  'Operations & Maintenance': '#f97316',
  'Governance & Board':       '#8b5cf6',
  'Communication & Documents':'#ec4899',
  'Resident Self-Service':    '#0ea5e9',
  'System Administration':    '#ef4444',
};

export const DIFFICULTY_COLORS: Record<string, string> = {
  Beginner:     '#52c41a',
  Intermediate: '#faad14',
  Advanced:     '#f5222d',
};

// ─── Tutorial Data ────────────────────────────────────────────────────────────

export const TUTORIALS: Tutorial[] = [

  // ════════════════════════════════════════════════════════
  //  GETTING STARTED  (all roles)
  // ════════════════════════════════════════════════════════

  {
    id: 'gs-01',
    title: 'Platform Overview & Navigation',
    description:
      'Understand the CondoManager platform structure, sidebar navigation groups, condo switching, and how to locate any feature within the application.',
    category: 'Getting Started',
    roles: ['super_admin_condo', 'admin', 'owner', 'board-member'],
    difficulty: 'Beginner',
    estimatedTime: '10 min',
    tags: ['navigation', 'basics', 'orientation'],
    icon: <RocketOutlined />,
    steps: [
      {
        title: 'Sign In and Authenticate',
        content:
          'Navigate to your organization\'s CondoManager URL. Enter your corporate email address and password on the login screen. If your organization has enabled multi-factor authentication, you will be prompted to enter a verification code sent to your registered device. Upon successful authentication, the system loads your role-specific dashboard automatically.',
        tip: 'If you forget your password, use the "Forgot Password" link on the login page. Password reset emails arrive within two minutes. Check your spam folder if it does not appear.',
      },
      {
        title: 'Understand the Sidebar Navigation',
        content:
          'The left sidebar is divided into labeled groups: Overview, Property, Residents, Finances, Operations, and System. Each group contains module links relevant to that functional domain. Groups can be collapsed or expanded by clicking the group header. The sidebar collapses to icon-only mode using the toggle button at the top, which is useful on smaller screens or when you need more workspace.',
        navigate: 'Sidebar — any group header',
        tip: 'Your role determines which sidebar items are visible. If you expect to see a module but do not, contact your system administrator to review your feature permissions.',
      },
      {
        title: 'Switch Between Properties',
        content:
          'If your account manages or is associated with multiple condominium properties, a property selector appears in the top navigation bar. Click the selector to view a dropdown list of available properties. Selecting a different property reloads all data — residents, fees, work orders, and reports — for that specific building. Always confirm the active property in the header before making changes to avoid editing the wrong property.',
        warning: 'All data entry, edits, and reports operate on the currently selected property. Verify the active condo before creating records.',
      },
      {
        title: 'Access Profile and Account Settings',
        content:
          'Click your name or avatar in the top-right corner to open the account menu. From here you can update your display name, email address, and notification preferences. Administrators can also adjust their default landing page. Changes take effect immediately upon saving.',
        navigate: 'Top-right avatar → Profile Settings',
      },
      {
        title: 'Use the Help and Support Resources',
        content:
          'The Learning Center (this page) provides role-specific guides for every platform module. For technical issues, contact your system administrator or submit a support ticket through your organization\'s helpdesk channel. When reporting issues, include your role, the active property name, and a description of the expected versus actual behavior to expedite resolution.',
        tip: 'Bookmark the Learning Center URL for quick access during onboarding or when training new team members.',
      },
    ],
  },

  // ════════════════════════════════════════════════════════
  //  SYSTEM ADMINISTRATION  (super_admin_condo only)
  // ════════════════════════════════════════════════════════

  {
    id: 'sa-01',
    title: 'Multi-Property Portfolio Management',
    description:
      'Configure and manage multiple condominium properties within a single platform instance, including property creation, settings, and cross-property reporting.',
    category: 'System Administration',
    roles: ['super_admin_condo'],
    difficulty: 'Intermediate',
    estimatedTime: '20 min',
    tags: ['portfolio', 'properties', 'setup'],
    icon: <BankOutlined />,
    steps: [
      {
        title: 'Access the Condominiums Module',
        content:
          'Navigate to Property → Condominiums in the left sidebar. This module is exclusively available to Super Administrators and displays a complete list of all properties in the portfolio with key metrics: total units, occupied units, occupancy rate, monthly fee, and managed-since date.',
        navigate: 'Property → Condominiums',
      },
      {
        title: 'Create a New Property Record',
        content:
          'Click the "+ Add Condominium" button in the top-right of the Condominiums page. Complete the property profile form: legal property name, civic address, city, province/state, postal/zip code, total unit count, monthly maintenance fee, yearly fee, primary contact email, and contact phone number. The "Managed Since" date records when your organization assumed management responsibility.',
        tip: 'Double-check the total unit count carefully — this drives occupancy rate calculations throughout the platform and cannot be updated retroactively without affecting historical reports.',
      },
      {
        title: 'Configure Property Financial Parameters',
        content:
          'After creating the property record, set the monthly and yearly fee amounts. These values populate the fee templates applied to resident accounts. If your property uses a tiered fee structure (different fees per unit type), create individual fee entries per unit after the base property is saved.',
        navigate: 'Finances → Fee Management (after switching to new property)',
      },
      {
        title: 'Switch Between Properties as Super Admin',
        content:
          'Use the property selector in the top navigation bar to switch between portfolio properties. As a Super Administrator, you have unrestricted access to all properties. When switching, confirm the property name displayed in the header before performing any administrative action. Each property maintains completely isolated data sets.',
      },
      {
        title: 'Assign Property Administrators',
        content:
          'Each property requires at least one Admin-role user to handle daily operations. Coordinate with your user management system to provision admin accounts with the correct property association. Admin users see only the properties they are assigned to; Super Administrators see all.',
        warning: 'Do not assign Super Administrator roles to operational staff. Use the Admin role for property managers and the Owner role for residents.',
      },
    ],
  },

  {
    id: 'sa-02',
    title: 'Feature Permissions & Access Control',
    description:
      'Customize which platform modules are visible and accessible for each role — Admin, Owner, and Board Member — on a per-feature basis.',
    category: 'System Administration',
    roles: ['super_admin_condo'],
    difficulty: 'Advanced',
    estimatedTime: '25 min',
    tags: ['permissions', 'access control', 'roles', 'security'],
    icon: <LockOutlined />,
    steps: [
      {
        title: 'Navigate to Feature Permissions',
        content:
          'From the sidebar, select System → Feature Permissions. This page is visible only to Super Administrators and presents a permission matrix where rows represent platform features and columns represent user roles (Admin, Owner, Board Member).',
        navigate: 'System → Feature Permissions',
      },
      {
        title: 'Understand the Permission Matrix',
        content:
          'Each cell in the matrix is a toggle switch. When a feature is enabled for a role, users of that role see the corresponding sidebar item and can access the module. When disabled, the module is completely hidden — the user cannot navigate to it directly or via URL. Super Administrators are always exempt from feature restrictions and see all modules regardless of settings.',
        tip: 'Start with the default configuration, observe how staff use the system for two weeks, then refine permissions based on actual usage patterns rather than guessing upfront.',
      },
      {
        title: 'Restrict Sensitive Financial Modules',
        content:
          'For properties where operational staff (Admin role) should not view financial statements or analytics, disable "Financial Statements" and "Analytics & Reports" for the Admin role. This is common in management companies where a separate finance team handles accounting and property managers handle only day-to-day operations.',
        warning: 'Restricting Fee Management from the Admin role will prevent property managers from logging manual payments and tracking overdue balances. Ensure you have an alternative workflow before disabling.',
      },
      {
        title: 'Configure Owner Self-Service Access',
        content:
          'Determine which features residents can access through the Resident Portal. Common enabled features include: Resident Portal (dashboard), Payments (view balance and make payments), Amenity Booking, Document Center (read-only), and Maintenance Requests (submit requests). Common disabled features include: Fee Management, Expense Tracking, Financial Statements, Violations (edit), and Board Management.',
      },
      {
        title: 'Set Board Member Visibility',
        content:
          'Board members typically require read access to Financial Statements and Expense Tracking for governance oversight, but should not create or edit operational records. Enable read-relevant modules for the Board Member role and coordinate with your application development team if you require more granular read-vs-write restrictions.',
      },
      {
        title: 'Test Permission Changes',
        content:
          'After saving permission changes, log out and log back in with a test account of the affected role to verify the sidebar reflects your configuration. Alternatively, ask a trusted colleague with the relevant role to confirm they see only the intended modules. Permission changes take effect immediately upon save without requiring a platform restart.',
        tip: 'Maintain a written log of permission changes with dates and rationale. This audit trail is invaluable during security reviews or when onboarding new administrators.',
      },
    ],
  },

  // ════════════════════════════════════════════════════════
  //  PROPERTY ADMINISTRATION  (admin, super_admin_condo)
  // ════════════════════════════════════════════════════════

  {
    id: 'pa-01',
    title: 'Dashboard & Key Performance Metrics',
    description:
      'Interpret the management dashboard, understand the key performance indicators displayed, and use dashboard shortcuts to navigate to priority work items.',
    category: 'Property Administration',
    roles: ['super_admin_condo', 'admin'],
    difficulty: 'Beginner',
    estimatedTime: '15 min',
    tags: ['dashboard', 'KPIs', 'metrics', 'overview'],
    icon: <BarChartOutlined />,
    steps: [
      {
        title: 'Read the Summary Statistics',
        content:
          'The Overview dashboard displays four primary statistics: Total Units, Occupied Units, Occupancy Rate, and Outstanding Fees. Occupancy Rate is calculated as (Occupied ÷ Total) × 100. A rate below 85% warrants investigation into vacant unit marketing or resident retention. Outstanding Fees reflects the total balance owed across all units as of the current date.',
        navigate: 'Overview → Dashboard',
      },
      {
        title: 'Review Recent Payment Activity',
        content:
          'The Recent Payments section lists the five most recent fee transactions including payer name, unit number, amount, and date. Use this section for quick daily reconciliation. If an expected payment does not appear, navigate to Finances → Fee Management and filter by the specific unit to investigate.',
      },
      {
        title: 'Identify Overdue Accounts',
        content:
          'The Overdue Fees panel highlights residents with balances past their due date. Click any overdue entry to navigate directly to that resident\'s fee record. Overdue accounts are color-coded by age: yellow (0–30 days), orange (31–60 days), and red (61+ days).',
        tip: 'Set a weekly calendar reminder to review the overdue panel every Monday morning. Early intervention — a friendly reminder email at 7 days — significantly improves collection rates.',
      },
      {
        title: 'Use Dashboard Navigation Shortcuts',
        content:
          'Clicking any statistic card on the dashboard navigates directly to the relevant management module. Clicking "Outstanding Fees" opens Fee Management, "Maintenance Requests" opens the Maintenance module, and "Work Orders" opens Work Order Management. These shortcuts eliminate several navigation clicks during busy periods.',
      },
    ],
  },

  // ════════════════════════════════════════════════════════
  //  RESIDENT MANAGEMENT  (admin, super_admin_condo)
  // ════════════════════════════════════════════════════════

  {
    id: 'rm-01',
    title: 'Resident Onboarding & Profile Management',
    description:
      'Complete the end-to-end process of adding a new resident to the platform: creating their profile, assigning their unit, configuring their fee schedule, and verifying portal access.',
    category: 'Resident Management',
    roles: ['super_admin_condo', 'admin'],
    difficulty: 'Intermediate',
    estimatedTime: '20 min',
    tags: ['onboarding', 'residents', 'units', 'setup'],
    icon: <TeamOutlined />,
    steps: [
      {
        title: 'Navigate to Resident Management',
        content:
          'Select Residents → Resident Management from the left sidebar. The page displays all current residents with their unit assignments, contact information, and account status. Use the search bar to confirm a new resident does not already have an existing profile before creating a duplicate.',
        navigate: 'Residents → Resident Management',
      },
      {
        title: 'Create the Resident Profile',
        content:
          'Click "+ Add Resident" to open the profile creation form. Required fields include: first and last name, unit number, primary email address, phone number, and move-in date. The email address serves as the resident\'s login credential for the Resident Portal — confirm its accuracy with the resident before saving.',
        warning: 'Email addresses must be unique in the system. If the address is already registered to another account, the system will reject the entry with a duplicate email error.',
      },
      {
        title: 'Assign the Unit',
        content:
          'In the unit number field, enter the resident\'s designated unit identifier as it appears in your property records (e.g., "204", "B-12", "PH1"). Unit numbers are free-text fields — maintain a consistent naming convention across all entries to ensure accurate filtering and reporting.',
      },
      {
        title: 'Set Up the Fee Schedule',
        content:
          'After saving the resident profile, navigate to Finances → Fee Management and create a fee entry for the new resident\'s unit. Set the fee type (Monthly Maintenance), monthly amount, and due date. The system begins tracking this fee against the resident\'s account from the specified start date.',
        navigate: 'Finances → Fee Management → + Add Fee',
      },
      {
        title: 'Send a Welcome Communication',
        content:
          'Navigate to Operations → Email Center and compose a welcome email to the new resident. Include their portal login URL, temporary credentials (if applicable), key contact information for property management, and links to important documents such as the building rules and move-in procedures checklist.',
        navigate: 'Operations → Email Center',
      },
      {
        title: 'Verify Portal Access',
        content:
          'Ask the new resident to confirm they can log in successfully at their first opportunity. Common onboarding issues include: typo in email address (re-verify and correct in the profile), spam filtering of welcome emails (advise resident to check spam), or expired temporary password (reset in the system). Follow up within 48 hours if the resident has not confirmed access.',
        tip: 'Create a standard onboarding email template in Email Center with your property name, key contacts, portal URL, and FAQs. This saves time for every new resident and ensures consistent communication.',
      },
    ],
  },

  {
    id: 'rm-02',
    title: 'Violation Management & Enforcement',
    description:
      'Issue violation notices, track their status through the resolution lifecycle, escalate repeat offences, and generate violation history reports for board review.',
    category: 'Resident Management',
    roles: ['super_admin_condo', 'admin'],
    difficulty: 'Intermediate',
    estimatedTime: '20 min',
    tags: ['violations', 'enforcement', 'notices', 'compliance'],
    icon: <WarningOutlined />,
    steps: [
      {
        title: 'Navigate to Violations',
        content:
          'Select Residents → Violations from the sidebar. The violations list displays all active and historical notices with status indicators: OPEN (issued, awaiting response), UNDER_REVIEW (evidence submitted or disputed), RESOLVED (corrective action confirmed), and CLOSED (archived).',
        navigate: 'Residents → Violations',
      },
      {
        title: 'Issue a New Violation Notice',
        content:
          'Click "+ New Violation". Select the resident\'s unit number, the rule or bylaw section violated (from a predefined list or free-text description), severity level (Warning, Minor, Major, Critical), and the observed violation date. Attach any supporting documentation such as photos, written complaints, or inspection reports. The system timestamps the notice creation automatically.',
      },
      {
        title: 'Notify the Resident',
        content:
          'After saving the violation, use the action menu to send a formal notice to the resident. The notice email includes the violation date, description, applicable rule, and the required corrective action with a response deadline (typically 14 days for warnings, 7 days for major violations). Retain a copy of all notices in the Document Center for legal record purposes.',
        warning: 'Ensure all violation notices comply with your condominium act\'s notice requirements regarding delivery method and cure periods. Consult your legal counsel if uncertain.',
      },
      {
        title: 'Track Status Through Resolution',
        content:
          'Update the violation status as the situation progresses. When the resident acknowledges and corrects the issue, change the status to RESOLVED and document the corrective action taken. For disputed violations, move to UNDER_REVIEW and record the resident\'s response. Close the record only after all parties confirm resolution.',
      },
      {
        title: 'Escalate Repeat Violations',
        content:
          'Filter the violations list by unit number to identify residents with multiple entries. Three or more violations of the same type within a 12-month period typically warrants escalation to the board. Create a Board Resolution (via Residents → Board Management → Resolutions) to formally address the pattern and authorize additional enforcement measures.',
      },
      {
        title: 'Generate a Violations Report',
        content:
          'Navigate to Finances → Analytics & Reports and run a Violations Summary report for the period under review. Export as PDF for board meeting inclusion or as Excel for further analysis. This report is a standard agenda item for monthly board meetings and helps identify systemic building issues.',
        navigate: 'Finances → Analytics & Reports',
      },
    ],
  },

  // ════════════════════════════════════════════════════════
  //  FINANCIAL MANAGEMENT
  // ════════════════════════════════════════════════════════

  {
    id: 'fm-01',
    title: 'Fee Management & Collections',
    description:
      'Configure fee schedules, track outstanding balances, process payments, apply late fees, and manage the full accounts receivable lifecycle for your property.',
    category: 'Financial Management',
    roles: ['super_admin_condo', 'admin'],
    difficulty: 'Intermediate',
    estimatedTime: '25 min',
    tags: ['fees', 'payments', 'collections', 'receivables'],
    icon: <DollarOutlined />,
    steps: [
      {
        title: 'Understand Fee Types',
        content:
          'CondoManager supports three primary fee categories: Monthly Maintenance Fees (recurring charges covering building operations), Special Assessments (one-time charges for major capital expenditures or emergency repairs), and Late Fees (penalty charges applied to overdue balances). Each fee type has its own ledger entry and appears separately on resident statements.',
        navigate: 'Finances → Fee Management',
      },
      {
        title: 'Create a Fee Entry',
        content:
          'Click "+ Add Fee". Select the resident or unit, choose the fee type, enter the amount, set the due date, and add any descriptive notes (e.g., "April 2025 Monthly Maintenance — Unit 304"). Save the entry to add it to the resident\'s account. The system immediately reflects this in the resident\'s outstanding balance.',
      },
      {
        title: 'Process a Manual Payment',
        content:
          'When a resident pays by cheque, e-transfer, or cash, locate their fee entry and click "Record Payment". Enter the payment amount, method, date received, and reference number (cheque number, transaction ID, or receipt number). Partial payments are supported — the system calculates the remaining balance automatically.',
        tip: 'Always obtain and retain proof of payment (bank deposit confirmation, email transfer notification) before marking fees as paid. Disputes over payment application are common and documentation is essential.',
      },
      {
        title: 'Apply Late Fees',
        content:
          'For accounts past their due date, create a new fee entry of type "Late Fee". Reference the original overdue fee entry number in the notes field to maintain a clear audit trail. Late fee amounts and grace periods should be defined in your condominium\'s fee collection policy and applied consistently across all units.',
        warning: 'Late fee amounts and application timing must comply with your jurisdiction\'s condominium legislation. Review your legal documents before applying penalties.',
      },
      {
        title: 'Generate Resident Statements',
        content:
          'Navigate to Finances → Financial Statements to generate a statement for a specific resident and date range. Statements show opening balance, all charges, all payments, and closing balance. Export as PDF to email to residents or print for physical mail distribution. Monthly statement distribution is a best practice for maintaining payment discipline.',
        navigate: 'Finances → Financial Statements',
      },
      {
        title: 'Review the Overdue Accounts Report',
        content:
          'From Analytics & Reports, run an "Accounts Receivable Aging" report to see all overdue balances segmented by age (0–30, 31–60, 61–90, 90+ days). Accounts in the 60+ day bracket should be flagged for formal collection letters. Accounts at 90+ days may require board authorization to refer to legal counsel.',
        navigate: 'Finances → Analytics & Reports',
      },
    ],
  },

  {
    id: 'fm-02',
    title: 'Expense Tracking & Budget Oversight',
    description:
      'Record and categorize all property expenditures, maintain accurate expense ledgers, reconcile against the budget, and prepare financial data for board review.',
    category: 'Financial Management',
    roles: ['super_admin_condo', 'admin'],
    difficulty: 'Intermediate',
    estimatedTime: '20 min',
    tags: ['expenses', 'budget', 'accounting', 'vendors'],
    icon: <DollarOutlined />,
    steps: [
      {
        title: 'Navigate to Expense Tracking',
        content:
          'Select Finances → Expense Tracking from the sidebar. The expenses register displays all recorded expenditures with vendor name, category, amount, date, and payment status. Use the date range filter to view expenses for a specific fiscal period.',
        navigate: 'Finances → Expense Tracking',
      },
      {
        title: 'Record a New Expense',
        content:
          'Click "+ Add Expense". Enter the vendor name, invoice number, expense category (Maintenance & Repairs, Utilities, Insurance, Management Fees, Capital Improvements, Administrative, or Other), amount, invoice date, and payment date. Attach the scanned invoice or receipt. Categories must match your chart of accounts to ensure accurate financial reporting.',
      },
      {
        title: 'Categorize Expenses Accurately',
        content:
          'Consistent categorization is critical for year-end financial statements and reserve fund studies. Operating expenses (regular maintenance, utilities, management fees) belong in current-year budget line items. Capital expenditures (roof replacement, elevator modernization, lobby renovation) may be classified separately and funded from the reserve fund — consult your accountant for guidance.',
      },
      {
        title: 'Track Vendor Payment Status',
        content:
          'Mark each expense as Pending (invoice received, not yet paid), Paid (payment processed), or Disputed (invoice contested with vendor). The aging of unpaid invoices appears in summary reports and helps prevent late payment penalties from vendors with net-30 or net-60 terms.',
      },
      {
        title: 'Reconcile Against Budget',
        content:
          'Navigate to Analytics & Reports and compare expense totals by category against your approved budget. A variance greater than 10% in any category warrants investigation and, if material, may require board notification. Document the reason for any significant variance in the expense record\'s notes field.',
        navigate: 'Finances → Analytics & Reports',
      },
    ],
  },

  {
    id: 'fm-03',
    title: 'Financial Reporting & Analytics',
    description:
      'Generate, interpret, and export the full suite of financial reports for management review, board presentations, and annual financial disclosure.',
    category: 'Financial Management',
    roles: ['super_admin_condo', 'admin'],
    difficulty: 'Advanced',
    estimatedTime: '25 min',
    tags: ['reports', 'analytics', 'statements', 'board'],
    icon: <BarChartOutlined />,
    steps: [
      {
        title: 'Access Analytics & Reports',
        content:
          'Navigate to Finances → Analytics & Reports. This module provides four primary report types: Revenue Summary (income by period and category), Expense Analysis (expenditures by vendor and category), Accounts Receivable Aging (outstanding balances by age bracket), and Occupancy Trends (occupancy rate over time).',
        navigate: 'Finances → Analytics & Reports',
      },
      {
        title: 'Generate a Monthly Revenue Summary',
        content:
          'Select "Revenue Summary" report, set the date range to the desired month, and click Generate. The report shows total fees charged, total payments received, net collection rate percentage, and outstanding balance. A collection rate below 95% should trigger a collections review.',
      },
      {
        title: 'Produce the Board Financial Package',
        content:
          'For monthly board meetings, generate and export four reports: (1) Revenue Summary for the month, (2) Expense Analysis for the month, (3) Accounts Receivable Aging, and (4) Year-to-Date comparison. Combine these into a single PDF package using your PDF management tool. Distribute to board members at least 48 hours before the meeting.',
        tip: 'Create a recurring calendar task for financial package preparation 5 days before each scheduled board meeting. This ensures you have adequate time for review and any corrections.',
      },
      {
        title: 'Export Data for External Accounting',
        content:
          'All reports support Excel export for import into accounting software such as QuickBooks, Sage, or Xero. Use the "Export to Excel" button on any report. The exported file includes all column headers and data rows in a format compatible with standard accounting import templates.',
      },
      {
        title: 'Review Annual Financial Disclosure',
        content:
          'Most jurisdictions require an annual financial statement to be distributed to all unit owners within a specified period (typically 60–90 days after fiscal year end). Use the Financial Statements module to generate the full-year summary, export as PDF, and use Email Center to distribute to all residents simultaneously.',
        warning: 'Annual financial disclosure requirements are legislated in most jurisdictions. Confirm your specific deadlines and required content with your legal counsel each year.',
      },
    ],
  },

  // ════════════════════════════════════════════════════════
  //  OPERATIONS & MAINTENANCE
  // ════════════════════════════════════════════════════════

  {
    id: 'om-01',
    title: 'Maintenance Request Management',
    description:
      'Receive, triage, assign, and close resident maintenance requests efficiently, maintaining clear communication throughout the repair lifecycle.',
    category: 'Operations & Maintenance',
    roles: ['super_admin_condo', 'admin'],
    difficulty: 'Beginner',
    estimatedTime: '15 min',
    tags: ['maintenance', 'requests', 'repairs', 'vendors'],
    icon: <ToolOutlined />,
    steps: [
      {
        title: 'Review Incoming Maintenance Requests',
        content:
          'Navigate to Operations → Maintenance. New requests submitted by residents appear with status OPEN. Each request includes the reporting resident\'s name, unit number, issue category (Plumbing, Electrical, HVAC, Structural, Common Area, Other), a description of the problem, and the reported date. Review new requests within one business day of submission.',
        navigate: 'Operations → Maintenance',
      },
      {
        title: 'Triage by Priority',
        content:
          'Assign a priority level to each request: URGENT (life-safety issues — gas leaks, flooding, no heat in winter, elevator entrapment), HIGH (significant impact on resident comfort or building integrity — major appliance failure, roof leak), MEDIUM (functional but non-critical issues — minor leaks, damaged fixtures), LOW (cosmetic or long-term planning items). Priority determines the response time SLA.',
        warning: 'URGENT requests require same-day acknowledgement and vendor dispatch. Failure to respond to safety-related issues promptly creates legal liability for the management corporation.',
      },
      {
        title: 'Assign to a Vendor',
        content:
          'In the request detail, enter the vendor name and contact information responsible for the repair. Set the scheduled date if a specific appointment has been booked. Update the status from OPEN to IN_PROGRESS once the vendor begins work. Contact information entered here is visible in the request record for reference during follow-up.',
      },
      {
        title: 'Update Status Throughout Repair',
        content:
          'Maintain current status throughout the repair: IN_PROGRESS when the vendor is actively working, COMPLETED when the repair is finished and verified. Add notes at each stage describing what work was performed, parts replaced, and any follow-up actions required. These notes form the maintenance history for the unit and building systems.',
      },
      {
        title: 'Close the Request',
        content:
          'Once the resident confirms satisfaction or you have verified completion independently, mark the request as COMPLETED. Record the actual completion date, total repair cost (for expense tracking), and any warranty information for parts or labour. Notify the resident of closure via email to formally conclude the service cycle.',
        tip: 'Follow up with residents 3 business days after closing a request to confirm satisfaction. This feedback loop improves resident relations and catches any incomplete repairs before they escalate.',
      },
    ],
  },

  {
    id: 'om-02',
    title: 'Work Order Management',
    description:
      'Create and manage internal work orders for planned maintenance, capital projects, and vendor-coordinated building work with full lifecycle tracking.',
    category: 'Operations & Maintenance',
    roles: ['super_admin_condo', 'admin'],
    difficulty: 'Intermediate',
    estimatedTime: '20 min',
    tags: ['work orders', 'vendors', 'projects', 'maintenance'],
    icon: <AuditOutlined />,
    steps: [
      {
        title: 'Understand Work Orders vs. Maintenance Requests',
        content:
          'Maintenance Requests are initiated by residents for unit or common area issues. Work Orders are created by management for planned, scheduled, or proactive building maintenance — preventive HVAC servicing, annual roof inspections, parking lot restriping, common area renovations. Work Orders may originate from completed maintenance requests, board decisions, or management planning.',
        navigate: 'Operations → Work Orders',
      },
      {
        title: 'Create a Work Order',
        content:
          'Click "+ Create Work Order". Complete: title (concise description), category (Maintenance, Repair, Inspection, Renovation, Capital, Emergency), priority (LOW/MEDIUM/HIGH/URGENT), estimated cost, vendor name and contact, scheduled date, and detailed description. The "Reported By" field identifies the initiating staff member for accountability tracking.',
      },
      {
        title: 'Start the Work Order',
        content:
          'When the vendor arrives or work begins, click "Start" to transition the status from OPEN to IN_PROGRESS. This timestamps the actual start date and notifies the management record. If a scheduled work order cannot proceed on its planned date, update the scheduled date field and add a note explaining the delay.',
      },
      {
        title: 'Complete and Close',
        content:
          'Upon completion, click "Complete". Enter the actual completion date, actual cost incurred, and a summary of work performed. If the actual cost differs significantly from the estimate, document the variance reason. The system records this history against the building\'s maintenance log for future reference and budget planning.',
        tip: 'Photograph completed work before and after for significant repairs. Attach photo documentation to the work order record. This evidence is invaluable for insurance claims and dispute resolution with vendors.',
      },
      {
        title: 'Cancel a Work Order',
        content:
          'If a work order is no longer required (issue resolved by other means, scope changed, budget not approved), use the Cancel action and document the cancellation reason. Cancelled work orders are retained in the system for audit purposes but are removed from the active workload view.',
      },
      {
        title: 'Review Work Order History',
        content:
          'Use the status filter buttons to view work orders by lifecycle stage: ALL, OPEN, IN_PROGRESS, COMPLETED, CANCELLED. Filter by date range in the table to identify recurring maintenance patterns. Repeated work orders on the same building system (e.g., pump failures, elevator issues) indicate a capital replacement may be needed.',
      },
    ],
  },

  {
    id: 'om-03',
    title: 'Amenity Administration & Booking Management',
    description:
      'Configure building amenities, set availability and fee structures, manage resident bookings, and handle approval workflows for premium amenity spaces.',
    category: 'Operations & Maintenance',
    roles: ['super_admin_condo', 'admin'],
    difficulty: 'Intermediate',
    estimatedTime: '20 min',
    tags: ['amenities', 'bookings', 'scheduling', 'reservations'],
    icon: <CalendarOutlined />,
    steps: [
      {
        title: 'Configure an Amenity',
        content:
          'Navigate to Operations → Amenity Management. Click "+ Add Amenity". Enter the amenity name (e.g., "Rooftop Terrace"), physical location (e.g., "Floor 12, Building A"), maximum capacity (persons), available hours (from/to), hourly fee (enter $0 for no charge), and whether reservations require prior management approval. Assign a calendar colour for visual distinction in the booking calendar.',
        navigate: 'Operations → Amenity Management',
      },
      {
        title: 'Set Approval Requirements',
        content:
          'Amenities with higher damage risk, noise potential, or limited capacity (party rooms, BBQ areas, guest suites) should be set to "Requires Approval". Residents submit a booking request and management reviews before confirming. Low-risk amenities (fitness room, library, bicycle storage) typically use Auto-Approve for resident convenience.',
      },
      {
        title: 'Review and Manage Bookings',
        content:
          'Navigate to Operations → Amenity Booking to see all reservations. Filter by amenity or date range. For approval-required amenities, pending bookings appear with an "Approve" or "Reject" action. Confirm the resident has no outstanding violations or unpaid fees before approving premium amenity bookings.',
        navigate: 'Operations → Amenity Booking',
      },
      {
        title: 'Handle Booking Conflicts',
        content:
          'The system prevents double-booking of the same amenity time slot. If a conflict exists, the second booking attempt is rejected with an availability error. If you need to override a booking (e.g., an emergency maintenance closure), cancel the resident booking, notify the resident immediately by email, and offer an alternative time. Provide a goodwill credit where appropriate.',
        warning: 'Always notify residents of cancellation at least 24 hours in advance where possible. Last-minute cancellations without communication are a leading source of resident complaints.',
      },
    ],
  },

  {
    id: 'om-04',
    title: 'Package Tracking Operations',
    description:
      'Log incoming deliveries, assign storage locations, notify residents, and manage the complete package custody chain from receipt to pickup.',
    category: 'Operations & Maintenance',
    roles: ['super_admin_condo', 'admin'],
    difficulty: 'Beginner',
    estimatedTime: '15 min',
    tags: ['packages', 'deliveries', 'tracking', 'concierge'],
    icon: <InboxOutlined />,
    steps: [
      {
        title: 'Log an Incoming Package',
        content:
          'When a package arrives at the building, navigate to Operations → Package Tracking and click "+ Log Package". Enter the tracking number (from the carrier label), carrier name (UPS, FedEx, Canada Post, Amazon, DHL, etc.), recipient\'s full name, recipient\'s unit number, and package storage location (Front Desk, Mailroom, Package Room A/B, Locker number). Add a brief description if the package has special handling notes (fragile, refrigerated, oversized).',
        navigate: 'Operations → Package Tracking',
      },
      {
        title: 'Notify the Resident',
        content:
          'After logging, click the "Notify" bell icon in the package row. This sends an automated email to the resident\'s registered address with the package details, storage location, and front desk hours. The package status automatically updates from RECEIVED to NOTIFIED, and the notification timestamp is recorded for accountability.',
        tip: 'Establish a building policy of notifying residents within 2 hours of package receipt during business hours. Timely notifications dramatically reduce package holding times and prevent mailroom overcrowding.',
      },
      {
        title: 'Mark as Picked Up',
        content:
          'When the resident collects their package, click the "Mark Picked Up" checkmark icon. The status updates to PICKED_UP and the pickup timestamp is recorded. Require residents to present identification for high-value packages or those explicitly marked for secure release.',
      },
      {
        title: 'Handle Unclaimed Packages',
        content:
          'Packages unclaimed after your building\'s holding period (commonly 10–14 days) should be escalated. Send a second notification reminder, then attempt phone contact. If still unclaimed, initiate a return to the carrier. Update the status to RETURNED and document the return tracking number.',
        warning: 'Never open or dispose of unclaimed packages. Return to carrier with original tracking intact. Your liability ends upon confirmed return to the carrier.',
      },
      {
        title: 'Review Package Status Dashboard',
        content:
          'Use the filter buttons to monitor packages by status: All, Received (logged but not notified), Notified (awaiting pickup), Picked Up (completed), and Returned. The "Awaiting Pickup" count at the top of the page gives an instant snapshot of current package room inventory.',
      },
    ],
  },

  {
    id: 'om-05',
    title: 'Inspection Management',
    description:
      'Schedule and conduct building inspections, record findings, assign corrective actions, and track reinspection workflows for regulatory compliance.',
    category: 'Operations & Maintenance',
    roles: ['super_admin_condo', 'admin'],
    difficulty: 'Intermediate',
    estimatedTime: '20 min',
    tags: ['inspections', 'compliance', 'building', 'safety'],
    icon: <FileSearchOutlined />,
    steps: [
      {
        title: 'Schedule an Inspection',
        content:
          'Navigate to Operations → Inspections. Click "+ Schedule Inspection". Select the inspection type (Fire Safety, Elevator, Mechanical, Common Area, Unit Entry, Roof & Structure, Parking), enter the inspector name, inspection firm, scheduled date, and any pre-inspection notes. Advance scheduling of at least 30 days is recommended for mandatory regulatory inspections.',
        navigate: 'Operations → Inspections',
      },
      {
        title: 'Record Inspection Findings',
        content:
          'After the inspection, update the record with findings. The status progresses through: SCHEDULED → IN_PROGRESS → PASSED, FAILED, or REQUIRES_REINSPECTION. Document all observations, deficiencies, and recommendations in the findings notes field. Attach the inspector\'s formal report as a document for complete records.',
      },
      {
        title: 'Assign Corrective Actions',
        content:
          'For FAILED or REQUIRES_REINSPECTION results, create corresponding Work Orders for each identified deficiency. Link the work order to the inspection record in the notes field. Set a completion deadline that satisfies the regulator\'s corrective action window. Failed fire safety or elevator inspections require immediate escalation.',
        warning: 'Regulatory inspection failures must be reported to the relevant authority and corrective action timelines strictly observed. Non-compliance can result in fines, building closure orders, or loss of insurance coverage.',
      },
      {
        title: 'Schedule the Reinspection',
        content:
          'Once corrective work is complete, schedule a reinspection and notify the original inspection authority. Create a new inspection record linked to the original by referencing the previous inspection ID in the notes. Upon passing reinspection, update both records to reflect the resolved status.',
      },
    ],
  },

  // ════════════════════════════════════════════════════════
  //  GOVERNANCE & BOARD  (admin, super_admin_condo, board-member)
  // ════════════════════════════════════════════════════════

  {
    id: 'gb-01',
    title: 'Board Membership Administration',
    description:
      'Maintain accurate board member records, manage term dates, track active and inactive members, and maintain proper governance documentation.',
    category: 'Governance & Board',
    roles: ['super_admin_condo', 'admin'],
    difficulty: 'Beginner',
    estimatedTime: '15 min',
    tags: ['board', 'governance', 'members', 'terms'],
    icon: <SafetyCertificateOutlined />,
    steps: [
      {
        title: 'Access Board Management',
        content:
          'Navigate to Residents → Board Management. The module contains three tabs: Board Members, Meetings, and Resolutions & Voting. The Board Members tab lists all current and past board members with their role, unit, term dates, and active status.',
        navigate: 'Residents → Board Management → Board Members',
      },
      {
        title: 'Add a Board Member',
        content:
          'Click "+ Add Board Member". Enter the member\'s full name, position (President, Vice-President, Secretary, Treasurer, Director at Large), unit number, email address, phone number, and term start and end dates. The term dates are critical for governance documentation and trigger automatic status changes at term expiry.',
      },
      {
        title: 'Manage Term Renewals and Departures',
        content:
          'When a board member\'s term ends or they resign, set their status to Inactive. Do not delete board member records — inactive records preserve governance history required for audits and legal proceedings. Create a new member record for the replacement, even if the same person is re-elected, to ensure clean term documentation.',
        warning: 'Governance documentation requirements vary by jurisdiction. Maintain board member records for a minimum of 7 years post-term in compliance with most corporate record-keeping regulations.',
      },
      {
        title: 'Verify Board Composition',
        content:
          'Regularly confirm that the board has the required number of members as specified in your declaration or bylaws (typically 3–7 directors). An incomplete board may lack quorum authority to pass resolutions or execute contracts. Alert the board chair if vacancies exist.',
      },
    ],
  },

  {
    id: 'gb-02',
    title: 'Board Meeting Management',
    description:
      'Schedule board meetings, prepare agenda packages, record attendance, document minutes, and maintain the official meeting register.',
    category: 'Governance & Board',
    roles: ['super_admin_condo', 'admin', 'board-member'],
    difficulty: 'Intermediate',
    estimatedTime: '25 min',
    tags: ['meetings', 'agenda', 'minutes', 'attendance'],
    icon: <CalendarOutlined />,
    steps: [
      {
        title: 'Schedule a Board Meeting',
        content:
          'In Board Management, select the Meetings tab and click "+ Schedule Meeting". Enter the meeting title (e.g., "Regular Board Meeting — May 2025"), meeting type (Regular, Special, Annual General Meeting), scheduled date and time, location or video conference link, and an initial agenda. Circulate the meeting notice to all board members and residents (for open meetings) per your bylaws\' required notice period.',
        navigate: 'Residents → Board Management → Meetings',
      },
      {
        title: 'Prepare the Agenda Package',
        content:
          'A standard board meeting agenda includes: Call to Order, Approval of Previous Minutes, Financial Report, Management Report, Maintenance & Operations Update, New Business, Old Business/Action Item Review, and Adjournment. Add agenda items to the meeting record. Attach supporting documents (financial reports, maintenance summaries, vendor quotes) to the Document Center and reference them in the meeting record.',
        tip: 'Distribute the complete agenda package to board members 72 hours before the meeting to allow adequate preparation time. Well-prepared directors make better decisions.',
      },
      {
        title: 'Record Meeting Attendance',
        content:
          'At the start of the meeting, update the Attendees field in the meeting record with the names of present board members. Quorum is typically a majority of board members (e.g., 3 of 5). Note the time quorum was achieved. Record any guests, legal counsel, or management representatives present as non-voting attendees.',
      },
      {
        title: 'Document Meeting Minutes',
        content:
          'During or immediately after the meeting, record concise minutes capturing: decisions made (with vote counts), action items (with owner and due date), key discussion points on contested matters, and any motions made, seconded, and voted upon. Minutes should be factual and objective — record decisions, not debates.',
        warning: 'Minutes are legal records and may be subpoenaed in legal proceedings. Ensure accuracy, completeness, and proper safekeeping. Have minutes approved at the next meeting before filing.',
      },
      {
        title: 'Approve and Archive Minutes',
        content:
          'At the subsequent meeting, table the previous meeting\'s draft minutes for approval. Upon board approval (moved, seconded, carried), update the minutes record status to Approved. Store approved minutes in the Document Center under the appropriate category. Approved minutes are available to all unit owners upon request in most jurisdictions.',
      },
    ],
  },

  {
    id: 'gb-03',
    title: 'Board Resolutions & Voting',
    description:
      'Draft formal board resolutions, conduct electronic votes, record results, and maintain the official resolution register for governance compliance.',
    category: 'Governance & Board',
    roles: ['super_admin_condo', 'admin', 'board-member'],
    difficulty: 'Intermediate',
    estimatedTime: '25 min',
    tags: ['resolutions', 'voting', 'governance', 'decisions'],
    icon: <CheckCircleOutlined />,
    steps: [
      {
        title: 'Understand When a Resolution is Required',
        content:
          'A formal board resolution is required for decisions that: exceed a defined expenditure threshold (typically $5,000–$25,000 depending on bylaws), create or amend contracts, authorize legal action, change rules or policies, approve capital expenditures from the reserve fund, or address matters specifically reserved for board authority in your governing documents.',
        navigate: 'Residents → Board Management → Resolutions & Voting',
      },
      {
        title: 'Draft a Resolution',
        content:
          'Click "+ New Resolution". The resolution should include: a clear title identifying the matter, a brief preamble ("WHEREAS the board has determined..."), and a resolution clause ("BE IT RESOLVED THAT the board authorizes..."). Select the category (Financial, Maintenance, Policy, Legal, Administrative, Capital) and set the vote status to Open. Assign the meeting in which this resolution is being considered.',
        tip: 'Use clear, unambiguous language in resolutions. Vague wording creates implementation problems and can be challenged. Have legal counsel review resolutions authorizing expenditures over $50,000.',
      },
      {
        title: 'Conduct the Vote',
        content:
          'Once the resolution is Open, board members can cast their votes: FOR (in favour), AGAINST (opposed), or ABSTAIN (recused or no position). The system records each vote with the member\'s name and timestamp. The resolution remains open until the chair closes it at the conclusion of the vote.',
      },
      {
        title: 'Record Vote Results',
        content:
          'Enter the final vote tally (votes for, against, abstain) in the resolution record. A resolution passes when it receives majority support (more votes FOR than AGAINST, excluding abstentions) unless your bylaws require a higher threshold (two-thirds, three-quarters) for specific matter types. Note the quorum count at the time of the vote.',
      },
      {
        title: 'Close and File the Resolution',
        content:
          'After the vote, close the resolution using the "Close Resolution" action and record the outcome (PASSED or FAILED). Closed resolutions are locked from further modification. File the resolution in the Document Center. If the resolution authorized an action, assign a follow-up task to the relevant staff member with a completion deadline.',
      },
    ],
  },

  // ════════════════════════════════════════════════════════
  //  COMMUNICATION & DOCUMENTS
  // ════════════════════════════════════════════════════════

  {
    id: 'cd-01',
    title: 'Email Communications & Scheduling',
    description:
      'Compose targeted building communications, schedule recurring notices, manage the email calendar, and review delivery history for all resident correspondence.',
    category: 'Communication & Documents',
    roles: ['super_admin_condo', 'admin'],
    difficulty: 'Beginner',
    estimatedTime: '15 min',
    tags: ['email', 'communications', 'notifications', 'notices'],
    icon: <MailOutlined />,
    steps: [
      {
        title: 'Access Email Center',
        content:
          'Navigate to Operations → Email Center. This module manages all building communications sent through the platform. The email schedule list displays pending and sent communications with recipient group, subject, scheduled time, and status.',
        navigate: 'Operations → Email Center',
      },
      {
        title: 'Create a New Communication',
        content:
          'Click "+ New Email". Select the recipient group: All Residents, Specific Units, Admin Staff Only, Board Members, or a custom selection. Enter the subject line (clear and specific — avoid generic subjects like "Building Notice"), compose the message body, and choose whether to send immediately or schedule for a future date and time.',
        tip: 'Use descriptive subject lines that identify the action required: "ACTION REQUIRED: Annual HVAC Filter Inspection — Unit Access Needed" is far more effective than "Building Maintenance Notice".',
      },
      {
        title: 'Schedule Recurring Notices',
        content:
          'For monthly fee reminders, seasonal maintenance notices, and regular building updates, use the scheduling feature to set up recurring emails. Define the recurrence pattern (monthly, quarterly, annual), start date, and end date. Scheduled emails appear in the email calendar for review before sending.',
      },
      {
        title: 'Review Communication History',
        content:
          'The sent email log shows all historical communications with timestamps. Use this log to verify that important notices (annual meeting invitations, fee change announcements, rule amendments) were sent and to resolve resident disputes about whether they received a notice.',
        warning: 'Email delivery confirmation is not guaranteed. For critical legal notices (lien registration warnings, enforcement notices), always follow up with registered mail or hand-delivered written notice.',
      },
    ],
  },

  {
    id: 'cd-02',
    title: 'Document Centre & Status Certificates',
    description:
      'Upload and organize building documents, control resident access to sensitive files, generate status certificates, and maintain a complete corporate document registry.',
    category: 'Communication & Documents',
    roles: ['super_admin_condo', 'admin'],
    difficulty: 'Intermediate',
    estimatedTime: '20 min',
    tags: ['documents', 'files', 'status certificates', 'records'],
    icon: <FileTextOutlined />,
    steps: [
      {
        title: 'Navigate to Document Centre',
        content:
          'Select Operations → Document Centre from the sidebar. Documents are organized by category: Governing Documents (declaration, bylaws, rules), Financial Documents (budgets, statements, audits), Meeting Records (agendas, minutes, notices), Contracts (vendor agreements, service contracts), Insurance, and Other.',
        navigate: 'Operations → Document Centre',
      },
      {
        title: 'Upload a Document',
        content:
          'Click "+ Upload Document". Select the file (PDF, Word, Excel formats supported; maximum 25MB per file), assign the category, enter a descriptive name, set the effective date, and choose the access level: Admin Only (restricted to management) or All Residents (visible in the resident portal). Add version notes if this document supersedes a previous version.',
      },
      {
        title: 'Manage Document Versions',
        content:
          'When an updated version of a document is issued (amended bylaws, restated rules), upload the new version and mark the previous version as Superseded in the document notes. Do not delete old versions — they serve as the historical record of the corporation\'s governing framework. Clearly name documents with version dates: "Rules and Regulations v2.3 — Amended March 2025".',
      },
      {
        title: 'Generate a Status Certificate',
        content:
          'Navigate to Operations → Status Certificates. Status certificates are formal legal documents required by purchasers of condominium units confirming the current status of fees, violations, and legal proceedings. Click "+ New Certificate", select the unit, enter the requesting party (purchaser\'s solicitor), set the requested date, and generate the certificate. Review for accuracy before issuing.',
        navigate: 'Operations → Status Certificates',
        warning: 'Status certificates carry statutory warranties. Errors or omissions can create significant financial liability. Review every certificate carefully before issuance. Most jurisdictions impose a 10-day issuance deadline from receipt of a valid request.',
      },
      {
        title: 'Control Document Access',
        content:
          'Regularly audit the document access settings to ensure sensitive documents (legal correspondence, insurance claims, personnel records) are set to Admin Only. Residents should have access to governing documents, financial summaries, meeting minutes (after approval), and notices. Restrict detailed expense records, vendor contracts, and legal documents to management.',
      },
    ],
  },

  // ════════════════════════════════════════════════════════
  //  RESIDENT SELF-SERVICE  (owner role)
  // ════════════════════════════════════════════════════════

  {
    id: 'rs-01',
    title: 'Resident Portal Orientation',
    description:
      'Get started with your Resident Portal — understand your dashboard, account status, available self-service features, and how to navigate the platform.',
    category: 'Resident Self-Service',
    roles: ['owner'],
    difficulty: 'Beginner',
    estimatedTime: '10 min',
    tags: ['portal', 'getting started', 'overview', 'residents'],
    icon: <HomeOutlined />,
    steps: [
      {
        title: 'Log Into the Resident Portal',
        content:
          'Visit the portal URL provided by your property management team. Enter your registered email address and the password you received in your welcome email. On your first login, you will be prompted to create a new password. Choose a strong password of at least 12 characters combining letters, numbers, and symbols. Your email address cannot be changed by you — contact your property manager if your email changes.',
        tip: 'Use a password manager to securely store your portal credentials. Avoid reusing passwords from other accounts to protect your personal and financial information.',
      },
      {
        title: 'Understand Your Dashboard',
        content:
          'Your Resident Portal dashboard shows: your unit number and building name, your current account balance (amount owing or credit), any outstanding maintenance requests you have submitted, upcoming amenity bookings, and recent building notices from management. Review this dashboard regularly to stay informed about your account and building status.',
      },
      {
        title: 'Find Your Current Balance',
        content:
          'Your outstanding balance appears prominently on the dashboard and in the Payments section. Green indicates a zero or credit balance. Red indicates an amount owing. Click the balance figure to navigate to the detailed fee ledger, which itemizes every charge and payment on your account.',
      },
      {
        title: 'Explore Available Features',
        content:
          'Based on your building\'s configuration, your portal may include: Payments (view and pay fees), Maintenance (submit repair requests), Amenity Booking (reserve common areas), Documents (access building documents), and Statements (view detailed account history). Some features may be limited by your property management\'s platform settings.',
      },
    ],
  },

  {
    id: 'rs-02',
    title: 'Making & Managing Payments',
    description:
      'View your fee balance, make online payments, review your payment history, and understand how payments are applied to your account.',
    category: 'Resident Self-Service',
    roles: ['owner'],
    difficulty: 'Beginner',
    estimatedTime: '10 min',
    tags: ['payments', 'fees', 'balance', 'residents'],
    icon: <DollarOutlined />,
    steps: [
      {
        title: 'View Your Outstanding Balance',
        content:
          'Navigate to Payments in the left sidebar. Your current balance shows the total amount due as of today, broken down by fee type and due date. Any fees past their due date are highlighted in orange or red depending on how overdue they are.',
        navigate: 'Payments',
      },
      {
        title: 'Submit an Online Payment',
        content:
          'Click "Make a Payment". Select the fee(s) you wish to pay, enter the payment amount (you may pay the full balance or a partial amount), select your payment method, and confirm. You will receive an email confirmation with a transaction reference number. Save this confirmation as proof of payment.',
        tip: 'Pay your maintenance fees a few days before the due date to allow processing time and avoid late fee application. Set a recurring calendar reminder for your monthly fee due date.',
      },
      {
        title: 'Review Payment History',
        content:
          'The payment history table shows all payments processed on your account with transaction dates, amounts, methods, and reference numbers. This history is useful for personal budgeting, tax preparation (if condo fees are deductible in your jurisdiction), and resolving any billing questions.',
      },
      {
        title: 'Understand Your Statement',
        content:
          'Navigate to Financial Statements to view your detailed account statement for any date range. The statement shows the opening balance, all charges applied (with dates and descriptions), all payments received (with dates and reference numbers), and the closing balance. Download as PDF for your personal records.',
        navigate: 'Financial Statements',
      },
      {
        title: 'Dispute a Charge',
        content:
          'If you believe a charge on your account is incorrect, do not simply withhold payment — this may result in late fees and enforcement action. Instead, contact your property manager directly with the specific charge details, your reasoning for the dispute, and any supporting documentation. Disputes are resolved faster when submitted in writing.',
        warning: 'Withholding maintenance fees pending a dispute is not recommended in most jurisdictions and may trigger lien proceedings regardless of the underlying dispute. Continue paying undisputed amounts while the dispute is being resolved.',
      },
    ],
  },

  {
    id: 'rs-03',
    title: 'Submitting Maintenance Requests',
    description:
      'Report maintenance issues effectively, provide the right information for fast resolution, and track your request through to completion.',
    category: 'Resident Self-Service',
    roles: ['owner'],
    difficulty: 'Beginner',
    estimatedTime: '15 min',
    tags: ['maintenance', 'requests', 'repairs', 'residents'],
    icon: <ToolOutlined />,
    steps: [
      {
        title: 'Identify What to Report',
        content:
          'Report maintenance issues for: your unit (plumbing leaks, appliance malfunctions, electrical issues, HVAC problems, pest sightings) and common areas (elevator malfunctions, hallway lighting, lobby damage, parking lot hazards, amenity equipment failures). Do not report issues outside your building\'s property unless they affect your building.',
        navigate: 'Maintenance (or Residents → Maintenance in the sidebar)',
      },
      {
        title: 'Create a Maintenance Request',
        content:
          'Click "+ New Request". Select the issue category (Plumbing, Electrical, HVAC, Structural, Common Area, Pest Control, Other), describe the problem in detail (what is happening, when it started, how severe it is, whether it is getting worse), and select the priority you believe is appropriate. Attach photos if possible — visual documentation significantly speeds up assessment and vendor dispatch.',
        tip: 'Be as specific as possible: "Bathroom sink drain is fully blocked and water is backing up into the tub" is far more actionable than "bathroom problem". Include your unit number even if pre-filled — confirm it is correct.',
      },
      {
        title: 'For Emergencies',
        content:
          'Life-safety emergencies (gas smell, flooding, fire, power outage, elevator entrapment) require immediate phone contact with your property management emergency line — do not wait to submit an online request. Post the emergency contact number in your unit where it is easily accessible. After calling, you may also submit an online request to create a formal record.',
        warning: 'For fires, gas leaks, or other life-threatening emergencies, call 911 first before contacting building management. Your safety is the priority.',
      },
      {
        title: 'Track Your Request',
        content:
          'After submission, you can view the status of your request in the Maintenance section. Status updates from OPEN to IN_PROGRESS when a vendor is assigned and work begins, to COMPLETED when the repair is finished. You may receive email notifications at each status change depending on your building\'s communication settings.',
      },
      {
        title: 'Confirm Completion',
        content:
          'Once you receive a completion notification, inspect the repair. If the work is satisfactory, no action is required. If the issue persists or the repair is incomplete, contact your property manager immediately, referencing the original request number. Do not submit a new request for the same unresolved issue — this creates duplicate records and delays resolution.',
      },
    ],
  },

  {
    id: 'rs-04',
    title: 'Booking Building Amenities',
    description:
      'Browse available amenities, check real-time availability, make reservations, and understand booking policies and your responsibilities as an amenity user.',
    category: 'Resident Self-Service',
    roles: ['owner'],
    difficulty: 'Beginner',
    estimatedTime: '10 min',
    tags: ['amenities', 'bookings', 'reservations', 'residents'],
    icon: <CalendarOutlined />,
    steps: [
      {
        title: 'Browse Available Amenities',
        content:
          'Navigate to Amenity Booking in the sidebar. The amenities grid displays all bookable spaces with details: location, capacity, available hours, hourly fee (or free), and booking policy (auto-approve or requires management approval). Review the amenity description for rules specific to each space.',
        navigate: 'Amenity Booking',
      },
      {
        title: 'Check Availability and Make a Booking',
        content:
          'Click on the amenity you wish to book. Select your desired date and time from the availability calendar. Available slots appear in green; booked slots appear in grey. Enter the duration of your booking and the number of guests. Review the booking summary and confirm.',
      },
      {
        title: 'Approval-Required Amenities',
        content:
          'If the amenity requires prior approval (typically party rooms, guest suites, rooftop terraces), your booking request is submitted to management for review. You will receive an email confirmation once your booking is approved or declined. Do not make arrangements (invitations, catering) until you receive approval confirmation.',
        tip: 'Book approval-required amenities well in advance — at least 2 weeks for private events. Peak periods (holidays, summer weekends) book quickly.',
      },
      {
        title: 'Understand Your Responsibilities',
        content:
          'As the booking resident, you are responsible for the amenity space during your reservation: any damage, excessive cleaning requirements, or rule violations during your booking may result in a security deposit claim, violation notice, or suspension of booking privileges. Review the amenity rules posted in the space before and after your booking.',
      },
      {
        title: 'Cancel a Booking',
        content:
          'If your plans change, cancel your booking as early as possible so other residents can use the space. Find your booking in the Amenity Booking section, click your reservation, and select Cancel. Cancellation policies vary by amenity — check whether late cancellations (within 24 hours) affect your deposit or future booking privileges.',
      },
    ],
  },

  // ════════════════════════════════════════════════════════
  //  BOARD MEMBER  (board-member, admin, super_admin_condo)
  // ════════════════════════════════════════════════════════

  {
    id: 'bm-01',
    title: 'Board Member Roles & Responsibilities',
    description:
      'Understand the legal framework of condominium board governance, your fiduciary duties as a director, and how to use the platform to fulfill your obligations.',
    category: 'Governance & Board',
    roles: ['board-member', 'super_admin_condo', 'admin'],
    difficulty: 'Beginner',
    estimatedTime: '15 min',
    tags: ['board', 'governance', 'fiduciary', 'duties'],
    icon: <SafetyCertificateOutlined />,
    steps: [
      {
        title: 'Your Role as a Director',
        content:
          'As a condominium board director, you are a trustee of the corporation responsible for governing the property in the best interests of all unit owners — not your personal interests or those of neighbors who elected you. Your three core fiduciary duties are: Duty of Loyalty (act in the corporation\'s interest), Duty of Care (make informed decisions), and Duty of Obedience (comply with governing documents and applicable law).',
      },
      {
        title: 'Decision-Making Authority',
        content:
          'The board acts collectively — individual directors have no authority to make decisions or commit corporation resources independently. All significant decisions require a properly constituted meeting with quorum present and a duly passed resolution. Familiarize yourself with the expenditure thresholds in your declaration that distinguish board authority from owner approval requirements.',
      },
      {
        title: 'Access to Information',
        content:
          'Your board member access in CondoManager allows you to view financial reports, meeting records, and resolutions. You may also view maintenance summaries and violation reports as part of your oversight function. If you require access to information not currently visible in your portal, speak with your property manager — they can arrange appropriate access through the Feature Permissions system.',
        navigate: 'Residents → Board Management',
      },
      {
        title: 'Confidentiality Obligations',
        content:
          'Information you access as a board member — resident financial information, legal matters, personnel issues, insurance claims — is strictly confidential. Do not share board-level information with other residents, family members, or third parties. Breaching confidentiality can expose you personally to legal liability and undermine the corporation\'s legal position in disputes.',
        warning: 'Director liability is real. Consult your property manager or legal counsel before acting on any matter outside your clear authority. When in doubt, bring it to the full board.',
      },
    ],
  },

  {
    id: 'bm-02',
    title: 'Financial Oversight as a Director',
    description:
      'Review and interpret financial reports, understand the reserve fund, identify budget variances, and fulfill your fiduciary duty for sound financial governance.',
    category: 'Governance & Board',
    roles: ['board-member', 'super_admin_condo', 'admin'],
    difficulty: 'Intermediate',
    estimatedTime: '25 min',
    tags: ['finance', 'oversight', 'budget', 'reserve fund'],
    icon: <DollarOutlined />,
    steps: [
      {
        title: 'Review Monthly Financial Statements',
        content:
          'At each board meeting, review the current month\'s financial statements provided by property management. Key figures to examine: collection rate (fees collected ÷ fees charged), expense-to-budget variance by category, and current cash position. A collection rate below 95% or an expense overrun above 10% in any category requires management\'s explanation.',
        navigate: 'Finances → Financial Statements',
      },
      {
        title: 'Understand the Reserve Fund',
        content:
          'The Reserve Fund is a legislatively mandated savings account for major capital replacements (roofs, elevators, windows, parking surfaces). Review the Reserve Fund Summary report to confirm: current balance, year\'s contributions, year\'s expenditures, and projected balance. The balance should track against your Reserve Fund Study projections. A significantly under-funded reserve is a material risk to unit owners.',
      },
      {
        title: 'Review the Accounts Receivable Aging Report',
        content:
          'The AR Aging report shows outstanding fee balances by age. Accounts in the 90+ day bracket should be discussed at each board meeting. The board should authorize the property manager to pursue legal collection (demand letter, lien registration) for accounts exceeding a defined threshold, which is typically set out in the corporation\'s collection policy.',
      },
      {
        title: 'Approve Significant Expenditures',
        content:
          'Your governing documents specify expenditure amounts that require board approval (e.g., purchases over $5,000). When management presents quotes for significant repairs or projects, review at least two competitive quotes, assess the necessity and timing, and pass a formal resolution authorizing the expenditure if the board agrees to proceed.',
        tip: 'Create a standing Expenditure Approval Policy resolution that defines thresholds clearly. This avoids case-by-case interpretation and speeds routine approvals while protecting the board\'s authority for major decisions.',
      },
      {
        title: 'Participate in the Annual Budget Process',
        content:
          'Each year, property management presents a draft operating budget for the coming fiscal year. As a board member, review: whether expense projections are realistic based on historical trends, whether the reserve fund contribution meets or exceeds the Reserve Fund Study recommendation, whether the resulting fee increase is legally compliant and communicated to owners on time. Approve the budget by board resolution.',
      },
    ],
  },
];

// ─── Role Mapping ─────────────────────────────────────────────────────────────

export const ROLE_DISPLAY: Record<string, { label: string; color: string; description: string }> = {
  super_admin_condo: {
    label:       'Super Administrator',
    color:       '#ef4444',
    description: 'Full platform access — property portfolio management, system configuration, and all operational modules.',
  },
  admin: {
    label:       'Property Manager',
    color:       '#3b82f6',
    description: 'Day-to-day property operations — residents, finances, maintenance, board support, and communications.',
  },
  owner: {
    label:       'Resident / Owner',
    color:       '#10b981',
    description: 'Resident self-service — payments, maintenance requests, amenity bookings, and document access.',
  },
  'board-member': {
    label:       'Board Member',
    color:       '#8b5cf6',
    description: 'Governance and oversight — meetings, resolutions, financial review, and strategic direction.',
  },
};

export const LEARNING_PATHS: Record<string, string[]> = {
  super_admin_condo: [
    'gs-01', 'sa-01', 'sa-02', 'pa-01', 'rm-01', 'fm-01', 'fm-03',
    'om-01', 'om-02', 'gb-01', 'gb-02', 'gb-03', 'cd-01', 'cd-02',
  ],
  admin: [
    'gs-01', 'pa-01', 'rm-01', 'rm-02', 'fm-01', 'fm-02', 'fm-03',
    'om-01', 'om-02', 'om-03', 'om-04', 'om-05',
    'gb-01', 'gb-02', 'gb-03', 'cd-01', 'cd-02',
  ],
  owner: ['gs-01', 'rs-01', 'rs-02', 'rs-03', 'rs-04'],
  'board-member': ['gs-01', 'bm-01', 'bm-02', 'gb-02', 'gb-03'],
};

// ─── Quick Reference Data ─────────────────────────────────────────────────────

export const QUICK_REF_SECTIONS = [
  {
    title: 'Role Permissions Overview',
    icon: <LockOutlined />,
    roles: ['super_admin_condo', 'admin', 'owner', 'board-member'],
    content: (
      <div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#fafafa' }}>
              {['Feature', 'Super Admin', 'Admin', 'Owner', 'Board Member'].map(h => (
                <th key={h} style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #f0f0f0', fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              ['Dashboard', '✓', '✓', '✓', '✓'],
              ['Condominiums', '✓', '—', '—', '—'],
              ['Resident Management', '✓', '✓', '—', '—'],
              ['Violations', '✓', '✓', '—', '✓ (view)'],
              ['Board Management', '✓', '✓', '—', '✓'],
              ['Fee Management', '✓', '✓', '—', '—'],
              ['Expense Tracking', '✓', '✓', '—', '✓ (view)'],
              ['Financial Statements', '✓', '✓', '✓', '✓ (view)'],
              ['Payments', '✓', '✓', '✓', '—'],
              ['Analytics & Reports', '✓', '✓', '—', '—'],
              ['Maintenance', '✓', '✓', '✓ (submit)', '—'],
              ['Work Orders', '✓', '✓', '—', '—'],
              ['Inspections', '✓', '✓', '—', '—'],
              ['Package Tracking', '✓', '✓', '—', '—'],
              ['Amenity Booking', '✓', '✓', '✓', '✓'],
              ['Amenity Management', '✓', '✓', '—', '—'],
              ['Document Centre', '✓', '✓', '✓ (read)', '✓ (read)'],
              ['Status Certificates', '✓', '✓', '—', '—'],
              ['Email Centre', '✓', '✓', '—', '—'],
              ['Parking Inventory', '✓', '✓', '—', '—'],
              ['Feature Permissions', '✓', '—', '—', '—'],
            ].map(([feat, ...vals]) => (
              <tr key={feat} style={{ borderBottom: '1px solid #f5f5f5' }}>
                <td style={{ padding: '7px 12px', fontWeight: 500 }}>{feat}</td>
                {vals.map((v, i) => (
                  <td key={i} style={{ padding: '7px 12px', color: v === '✓' ? '#52c41a' : v === '—' ? '#d9d9d9' : '#fa8c16' }}>
                    {v}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
  },
  {
    title: 'Work Order Status Lifecycle',
    icon: <AuditOutlined />,
    roles: ['super_admin_condo', 'admin'],
    content: (
      <Steps
        direction="horizontal"
        size="small"
        current={-1}
        items={[
          { title: 'OPEN',        description: 'Work order created, vendor not yet started' },
          { title: 'IN PROGRESS', description: 'Vendor assigned and work underway' },
          { title: 'COMPLETED',   description: 'Work finished, actual cost recorded' },
        ]}
        style={{ marginTop: 8 }}
      />
    ),
  },
  {
    title: 'Package Tracking Lifecycle',
    icon: <InboxOutlined />,
    roles: ['super_admin_condo', 'admin'],
    content: (
      <Steps
        direction="horizontal"
        size="small"
        current={-1}
        items={[
          { title: 'RECEIVED',   description: 'Package logged, resident not yet notified' },
          { title: 'NOTIFIED',   description: 'Resident has been informed of delivery' },
          { title: 'PICKED UP',  description: 'Resident collected the package' },
          { title: 'RETURNED',   description: 'Returned to carrier unclaimed' },
        ]}
        style={{ marginTop: 8 }}
      />
    ),
  },
  {
    title: 'Maintenance Request Priority Guide',
    icon: <WarningOutlined />,
    roles: ['super_admin_condo', 'admin', 'owner'],
    content: (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {[
          { level: 'URGENT', color: '#ef4444', bg: '#fef2f2', sla: 'Same day', examples: 'Gas leak, flooding, fire, elevator entrapment, no heat (winter), sewage backup' },
          { level: 'HIGH',   color: '#f97316', bg: '#fff7ed', sla: '24–48 hrs', examples: 'Major appliance failure, significant water leak, security system failure, burst pipe' },
          { level: 'MEDIUM', color: '#eab308', bg: '#fefce8', sla: '3–5 days',  examples: 'Minor leaks, HVAC noise, damaged fixtures, partial elevator issues, pest sighting' },
          { level: 'LOW',    color: '#6b7280', bg: '#f9fafb', sla: '5–14 days', examples: 'Cosmetic damage, minor squeaks, paint scuffs, non-urgent preventive maintenance' },
        ].map(({ level, color, bg, sla, examples }) => (
          <div key={level} style={{ background: bg, border: `1px solid ${color}30`, borderRadius: 6, padding: 12 }}>
            <Tag color={color} style={{ marginBottom: 4 }}>{level}</Tag>
            <div style={{ fontSize: 12, color: '#595959' }}>
              <div><strong>Response SLA:</strong> {sla}</div>
              <div style={{ marginTop: 4 }}><strong>Examples:</strong> {examples}</div>
            </div>
          </div>
        ))}
      </div>
    ),
  },
  {
    title: 'Status Certificate Checklist',
    icon: <SafetyCertificateOutlined />,
    roles: ['super_admin_condo', 'admin'],
    content: (
      <div style={{ fontSize: 13 }}>
        <Paragraph>Before issuing a Status Certificate, confirm all of the following are accurate:</Paragraph>
        {[
          'Unit number and legal description match the request',
          'Monthly common expense fee is current and correct',
          'Outstanding fee balance (if any) is accurately stated',
          'Any special assessment details are complete',
          'Pending litigation or claims are disclosed',
          'Reserve fund summary reflects current balance',
          'Any leasehold or rental restrictions are noted',
          'No outstanding violation orders exist against the unit',
          'Management fees and administration fee charged matches policy',
          'Certificate is signed by an authorized officer of the corporation',
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'flex-start' }}>
            <CheckOutlined style={{ color: '#6366f1', marginTop: 2, flexShrink: 0 }} />
            <Text style={{ fontSize: 13 }}>{item}</Text>
          </div>
        ))}
        <Alert
          style={{ marginTop: 12, fontSize: 12 }}
          type="warning"
          message="Most jurisdictions impose a 10-day statutory deadline from receipt of a valid request and fee. Confirm your local requirements annually."
          showIcon
        />
      </div>
    ),
  },
  {
    title: 'Board Meeting Agenda Template',
    icon: <CalendarOutlined />,
    roles: ['super_admin_condo', 'admin', 'board-member'],
    content: (
      <div style={{ fontSize: 13 }}>
        {[
          ['1. Call to Order', 'Chair confirms quorum is present and calls the meeting to order. Record time.'],
          ['2. Approval of Agenda', 'Motion to adopt the circulated agenda. Seconded and voted.'],
          ['3. Approval of Previous Minutes', 'Motion to approve minutes from prior meeting. Seconded and voted.'],
          ['4. Financial Report', 'Property manager presents monthly financial statements. Board reviews and asks questions.'],
          ['5. Management Report', 'Update on operations: maintenance, work orders, violations, packages, inspections.'],
          ['6. Old Business', 'Status updates on action items from prior meetings.'],
          ['7. New Business', 'New motions, vendor approvals, policy changes, resident matters.'],
          ['8. In Camera Session', 'Confidential matters: legal issues, personnel, collections. Non-members excluded.'],
          ['9. Action Items Summary', 'Review all action items assigned during the meeting with owners and due dates.'],
          ['10. Adjournment', 'Motion to adjourn. Record time of adjournment.'],
        ].map(([title, desc]) => (
          <div key={title} style={{ marginBottom: 10 }}>
            <Text strong style={{ display: 'block', fontSize: 13 }}>{title}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{desc}</Text>
          </div>
        ))}
      </div>
    ),
  },
];
